import { useEffect, useState } from 'react';
import styles from './TestsPage.module.css';
import API from '../../../Utils/api';
import { useUser } from '../../../Context/useUser';
import { useParams } from 'react-router';
import { useVisitingUser } from '../../../Hooks/useVisitingUser';
import Table from '../../../Components/Table/Table';
import Utils from '../../../Utils';
import { calculateAerobicTrainingZones, calculateAnaerobicTrainingZones } from './zones';
import { toast } from 'react-toastify';

const AEROBIC_ZONES = [100, 200, 300, 400];

const ANAEROBIC_ZONES = [25, 50];

export default function TrainingZonesPage () {
    const { user } = useUser();

    const { athleteId } = useParams();

    const visitedUser = useVisitingUser(athleteId);

    const currentUser = athleteId ? visitedUser : user;

    const [tests, setTests] = useState([]);

    const [selectedTest, setSelectedTest] = useState();

    useEffect(() => {
        if (tests.length > 0) {
            setSelectedTest(tests[0]);
        }
    }, [tests]);

    const handleTestSelection = event => {
        const selectedTestId = event.target.value;
        const test = tests.find(test => test._id === selectedTestId);
        setSelectedTest(test);
    };

    const [selectedAerobicZone, setSelectedAerobicZone] = useState(AEROBIC_ZONES[0]);

    const handleAerobicZoneChange = event => {
        const selectedZone = parseInt(event.target.value, 10);
        setSelectedAerobicZone(selectedZone);
    };

    const handleAnaerobicZoneChange = event => {
        const selectedZone = parseInt(event.target.value, 10);
        setSelectedAnaerobicZone(selectedZone);
    };

    const [selectedAnaerobicZone, setSelectedAnaerobicZone] = useState(ANAEROBIC_ZONES[0]);

    const aerobicColumns = [
        {
            id: 'title',
            Header: 'Zone',
            accessor: 'title',
            Cell: ({ row }) => <div className={styles.zoneTitle}>{row.original.title}</div>
        },
        {
            id: 'subTitle',
            Header: 'Focus',
            accessor: 'subTitle',
            Cell: ({ row }) => <div>{row.original.subTitle}</div>
        },
        {
            id: 'paceGroup',
            Header: () => <div className={styles.center}>Pace</div>,
            columns: [
                {
                    id: 'timeFrom',
                    Header: () => <div className={styles.center}>Time From</div>,
                    accessor: 'timeFrom',
                    Cell: ({ row }) => <div className={styles.center}>{row.original.timeFrom}</div>
                },
                {
                    id: 'timeTo',
                    Header: () => <div className={styles.center}>Time To</div>,
                    accessor: 'timeTo',
                    Cell: ({ row }) => <div className={styles.center}>{row.original.timeTo}</div>
                },
                {
                    id: 'averagePace',
                    Header: () => <div className={styles.center}>Average</div>,
                    accessor: 'averagePace',
                    Cell: ({ row }) => <div className={styles.center}>{row.original.averagePace}</div>
                }
            ]
        },
        {
            id: 'totalDuration',
            Header: () => <div className={styles.center}>Total Duration</div>,
            accessor: 'totalDuration',
            Cell: ({ row }) => <div className={styles.center}>{row.original.totalDuration ?? 'N/A for 100m'}</div>
        }
    ];

    const anaerobicColumns = [
        {
            Header: 'Zone',
            accessor: 'title',
            Cell: ({ row }) => <div className={styles.zoneTitle}>{row.original.title}</div>
        },
        {
            Header: 'Focus',
            accessor: 'subTitle',
            Cell: ({ row }) => <div>{row.original.subTitle}</div>
        },
        {
            id: 'timeFrom',
            Header: () => <div className={styles.center}>Time From</div>,
            accessor: 'timeFrom',
            Cell: ({ row }) => <div className={styles.center}>{row.original.timeFrom}</div>
        },
        {
            id: 'timeTo',
            Header: () => <div className={styles.center}>Time To</div>,
            accessor: 'timeTo',
            Cell: ({ row }) => <div className={styles.center}>{row.original.timeTo}</div>
        },
        {
            id: 'averagePace',
            Header: () => <div className={styles.center}>Average</div>,
            accessor: 'averagePace',
            Cell: ({ row }) => <div className={styles.center}>{row.original.averagePace}</div>
        }
    ];

    useEffect(() => {
        fetchAndSetData(currentUser);
    }, [currentUser]);

    const fetchAndSetData = async currentUser => {
        if (!currentUser) return;

        try {
            const { data } = await API.getAllTestsForUser(currentUser._id);
            if (data) {
                setTests(data);
            }
        } catch (error) {
            toast.error(`Failed to fetch tests - please contact support`, {
                position: 'bottom-center'
            });
        }
    };

    return (
        <div className={styles.testPage} data-tests-page>
            <section className={styles.panel} aria-labelledby='zones-title'>
                <div className={styles.panelHead}>
                    <div className={styles.panelHeadText}>
                        <h2 id='zones-title' className={styles.panelTitle}>
                            Training zones
                        </h2>
                        <p className={styles.panelSub}>
                            Aerobic zones use the selected test's CSS; anaerobic zones use its MSS.
                        </p>
                    </div>
                </div>
                <div className={styles.panelBody}>
                    <div className={styles.picker}>
                        <label htmlFor='zones-test' className={styles.pickerLabel}>
                            Test
                        </label>
                        <select
                            id='zones-test'
                            className={`${styles.pickerSelect} form-control`}
                            onChange={handleTestSelection}
                        >
                            {tests.map(test => (
                                <option key={test._id} value={test._id}>
                                    {test.type} - {new Date(test.testDate).toLocaleDateString()} -{' '}
                                    {'CSS: ' + test.css?.toFixed(2)} -{' '}
                                    {'MSS: ' + test.mss?.toFixed(2)}
                                </option>
                            ))}
                        </select>
                    </div>
                    {tests.length === 0 && (
                        <p className={styles.emptyNote}>Complete a test to calculate your training zones.</p>
                    )}
                </div>
            </section>
            <section className={styles.panel} aria-labelledby='zones-aerobic-title'>
                <div className={styles.panelHead}>
                    <div className={styles.panelHeadText}>
                        <h3 id='zones-aerobic-title' className={styles.panelTitle}>
                            Aerobic zones
                        </h3>
                        <p className={styles.panelSub}>Pace per repeat distance, from CSS.</p>
                    </div>
                    <div className={styles.picker}>
                        <label htmlFor='zones-aerobic-distance' className={styles.pickerLabel}>
                            Aerobic distance
                        </label>
                        <select
                            id='zones-aerobic-distance'
                            onChange={handleAerobicZoneChange}
                            className={`${styles.distanceSelect} form-control`}
                        >
                            {AEROBIC_ZONES.map(zone => (
                                <option key={zone} value={zone}>
                                    {zone}m
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={styles.panelFlush}>
                    <Table
                        columns={aerobicColumns}
                        data={Utils.arrayfyObject(
                            calculateAerobicTrainingZones(selectedTest?.css, selectedAerobicZone),
                            {
                                keyToName: 'name'
                            }
                        )}
                        updatePageSize={false}
                        pageSkipping={false}
                        showSelectionIndicator={false}
                        tableLayout='fixed'
                    />
                </div>
            </section>
            <section className={styles.panel} aria-labelledby='zones-anaerobic-title'>
                <div className={styles.panelHead}>
                    <div className={styles.panelHeadText}>
                        <h3 id='zones-anaerobic-title' className={styles.panelTitle}>
                            Anaerobic zones
                        </h3>
                        <p className={styles.panelSub}>Pace per repeat distance, from MSS.</p>
                    </div>
                    <div className={styles.picker}>
                        <label htmlFor='zones-anaerobic-distance' className={styles.pickerLabel}>
                            Anaerobic distance
                        </label>
                        <select
                            id='zones-anaerobic-distance'
                            onChange={handleAnaerobicZoneChange}
                            className={`${styles.distanceSelect} form-control`}
                        >
                            {ANAEROBIC_ZONES.map(zone => (
                                <option key={zone} value={zone}>
                                    {zone}m
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className={styles.panelFlush}>
                    <Table
                        columns={anaerobicColumns}
                        data={Utils.arrayfyObject(
                            calculateAnaerobicTrainingZones(selectedTest?.mss, selectedAnaerobicZone),
                            {
                                keyToName: 'name'
                            },
                        )}
                        updatePageSize={false}
                        pageSkipping={false}
                        showSelectionIndicator={false}
                        tableLayout='fixed'
                    />
                </div>
            </section>
        </div>
    );
}
